import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { NotificationBarService } from './services/notification-bar.service';
import { isScreenWithoutMenus } from './menu/menu-visibility-helper';
import { ActivatedRoute } from '@angular/router';
import { BrandingDataService } from 'shared/branding';
import { AuthenticationService } from './authentication/authentication.service';
import { MatIconRegistry } from '@angular/material/icon';
import { FeatureConfigurationService } from './services/api/feature-configuration.service';
import { AppUpdateService, sentryLogger } from 'shared/core';
import { PageVisitService } from './services/util/page-visit.service';
import { AppStartupService } from './services/startup/app-startup.service';
import { User } from 'oidc-client-ts';
import { merge } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ConnectivtyBannerService } from './util/interceptors/connectivty-banner.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    BrandingDataService,
    FeatureConfigurationService,
    PageVisitService,
    AppStartupService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly _updates = inject(AppUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthenticationService);
  private readonly appStartupService = inject(AppStartupService);
  private readonly titleService = inject(Title);
  protected readonly notificationBarService = inject(NotificationBarService);
  private readonly route = inject(ActivatedRoute);
  private readonly brandingService = inject(BrandingDataService);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);

  protected readonly connectivity = inject(ConnectivtyBannerService);

  protected isLoading$ = signal(true);
  protected notificationBarVisible = false;
  protected menubarVisible = false;

  ngOnInit() {
    this.titleService.setTitle($localize`blankmile`);
    this.applyBranding();
    this.registerIcons();
    this.observeUserSession();
  }

  activatedComponentChanged(event: unknown) {
    this.notificationBarVisible = !isScreenWithoutMenus(event);
    this.menubarVisible = !isScreenWithoutMenus(event);
    if (this.route.snapshot.queryParams['hideMenubar'] === 'true') {
      this.menubarVisible = false;
    }
  }

  private registerIcons() {
    this.iconRegistry.addSvgIcon(
      'whatsapp',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/whatsapp.svg',
      ),
    );
    this.iconRegistry.addSvgIcon(
      'whatsapp_white',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/whatsapp-white.svg',
      ),
    );
    this.iconRegistry.addSvgIcon(
      'animated_images',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/animated_images.svg',
      ),
    );
    this.iconRegistry.addSvgIcon(
      'stylus_laser_pointer',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/stylus_laser_pointer.svg',
      ),
    );

    this.iconRegistry.addSvgIcon(
      'mode_cool_off',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/mode_cool_off.svg',
      ),
    );

    this.iconRegistry.addSvgIcon(
      'resume',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/resume.svg',
      ),
    );
  }

  private applyBranding() {
    this.brandingService
      .applyBranding()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading$.set(false);
        },
        error: (err) => {
          sentryLogger.error('Failed to apply helper branding', { err });
          this.isLoading$.set(false);
        },
      });
  }

  private observeUserSession(): void {
    this.destroyRef.onDestroy(() => this.appStartupService.tearDown());
    merge(this.authService.getUser(), this.authService.user$)
      .pipe(
        distinctUntilChanged((previous: User | null, current: User | null) => {
          return (
            previous?.profile?.sub === current?.profile?.sub &&
            previous?.access_token === current?.access_token &&
            previous?.expired === current?.expired
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        this.appStartupService.startUp(user);
      });
  }
}
